import { type RefObject, useEffect, useRef } from "react";

/** Close enough to the bottom edge to count as "still following the
    conversation" rather than "reading back through history". */
const NEAR_BOTTOM_PX = 48;

/**
 * Keeps a scroll container pinned to its bottom edge — the newest message
 * sits just above whatever follows it (a composer) — unless the reader has
 * scrolled up to look at older history, in which case new content no longer
 * yanks them back down.
 *
 * Re-anchors on three triggers: mount, a change to `messages`/`outbox`
 * (a message arrived or was sent), and any resize of the container itself.
 * That last one is what keeps the list pinned to the bottom when the
 * on-screen keyboard opens or closes: the app root a consuming screen lives
 * in is sized to the visible viewport (the client app's `useAppViewport`),
 * so the container genuinely shrinks — identically in a browser tab and an
 * installed app — and a ResizeObserver sees it either way.
 */
export function useStickToBottom(
  containerRef: RefObject<HTMLDivElement | null>,
  messages: unknown[],
  outbox: unknown[],
): void {
  const stuckRef = useRef(true);

  useEffect(() => {
    const el = containerRef.current;
    if (!el) {
      return;
    }

    function isNearBottom(target: HTMLDivElement): boolean {
      return target.scrollHeight - target.scrollTop - target.clientHeight <= NEAR_BOTTOM_PX;
    }

    function handleScroll() {
      stuckRef.current = isNearBottom(el!);
    }

    handleScroll();
    el.addEventListener("scroll", handleScroll, { passive: true });

    let observer: ResizeObserver | undefined;
    if (typeof ResizeObserver !== "undefined") {
      observer = new ResizeObserver(() => {
        if (stuckRef.current) {
          el!.scrollTop = el!.scrollHeight;
        }
      });
      observer.observe(el);
    }

    return () => {
      el.removeEventListener("scroll", handleScroll);
      observer?.disconnect();
    };
  }, [containerRef]);

  useEffect(() => {
    const el = containerRef.current;
    if (el && stuckRef.current) {
      el.scrollTop = el.scrollHeight;
    }
  }, [containerRef, messages, outbox]);
}
